import {
  ApprovalRule,
  type AutoApprover,
  Directory,
  Filter,
  IdpGroup,
  IntegrationResourceRule,
  RequestorRule,
  ResourceRule,
  RoutingRule,
  type TicketApprover,
} from "@p0security/shared/types/workflow/types";
import yaml from "js-yaml";
import stringify from "json-stable-stringify";
import { useCallback } from "react";
import { assertNever } from "utils/assert";

import { ApprovalRulePreview } from "./components/previews/ApprovalPreview";
import { RequestorRulePreview } from "./components/previews/RequestorPreview";
import { ResourceRulePreview } from "./components/previews/ResourcePreview";
import {
  DEFAULT_NEW_REQUESTOR,
  DEFAULT_NEW_RESOURCE,
  ResourceFilterType,
  filterKeyOptionsByResource,
  filterTypeOptionsByResource,
} from "./constants";

export const EXAMPLE_RULE = {
  name: "Example Rule",
  requestor: {
    type: "group",
    directory: "okta",
    id: "00g5j4jojlGZMzfhM697",
    label: "Engineering",
  },
  resource: {
    type: "integration",
    service: "snowflake",
  },
  approval: [
    {
      type: "group",
      id: "dataops@yourorg.com",
      label: "Data Ops Team",
      directory: "workspace",
      options: {
        allowOneParty: false,
      },
    },
  ],
};

export const EXAMPLE_DOCUMENT = yaml.dump([EXAMPLE_RULE], { flowLevel: 4 });

export const DEFAULT_RULE = {
  name: "Default p0 rule",
  requestor: DEFAULT_NEW_REQUESTOR,
  resource: DEFAULT_NEW_RESOURCE,
  approval: [
    {
      options: {
        allowOneParty: false,
        requireReason: false,
      },
      type: "p0",
    },
  ],
};

export const DEFAULT_DOCUMENT = yaml.dump([DEFAULT_RULE], { flowLevel: 4 });

export enum RoutingRulesView {
  YAML = "yaml",
  TABULAR = "tabular",
}

// All the logic for generating unique values and filters

export const getUniqueResources = (jsonRules: any[]) => {
  return Array.from(
    new Set(jsonRules.map((rule) => rule.resource && rule.resource.type))
  );
};

export const getResourceFilters = (uniqueResources: string[]) => {
  return uniqueResources.map((resource) => ({
    text: resource,
    value: resource,
  }));
};

// Typescript can't narrow integration resource filters from the keyed union to
// the single Filter value type — the cast localizes the workaround.
export const getResourceFilterEntries = (
  resource: ResourceRule | undefined
): [string, Filter][] => {
  if (resource?.type !== "integration") return [];
  return Object.entries(resource.filters ?? {}) as [string, Filter][];
};

export const useOnFilter = (dataIndex: string) => {
  return useCallback(
    (value: any, record: any) => {
      if (record[dataIndex] === undefined) {
        return false;
      }
      const yamlContent = yaml.dump(record[dataIndex]);
      return yamlContent.toLowerCase().includes(value.toLowerCase());
    },
    [dataIndex]
  );
};

export const getDefaultResourcePreviewRule = (
  type: ResourceRule["type"]
): ResourceRulePreview =>
  type === "integration" ? { type: "genericIntegration" } : { type };

export const getDefaultRequestorPreviewRule = (
  type: RequestorRule["type"]
): RequestorRulePreview => {
  switch (type) {
    case "agentic":
      return { type, agent: { type: "any" }, user: { type: "any" } };
    case "any":
      return { type };
    case "user":
      return { type, uid: "Specific user by email address" };
    case "group":
      return { type: "genericGroup" };
    case "wif":
      return { type, provider: "gcp", parent: "", pool: "" };
    default:
      return assertNever(type);
  }
};

export const getDefaultApprovalPreviewRule = (
  type: ApprovalRule["type"],
  installed: { auto: AutoApprover[]; ticket: TicketApprover[] }
): ApprovalRulePreview => {
  const [auto] = installed.auto;
  const [ticket] = installed.ticket;
  switch (type) {
    case "auto":
      if (!auto) throw new Error("integration required for auto type");
      return { type, integration: auto };
    case "ticket":
      if (!ticket) throw new Error("integration required for ticket type");
      return { type, integration: ticket, namespace: "", mapping: {} };
    case "group":
      return { type: "genericGroup" };
    case "requestor-profile":
      return { type, profileProperty: "", directory: "okta" };
    case "p0":
    case "deny":
    case "persistent":
      return { type };
    case "escalation":
      if (!auto) throw new Error("integration required for escalation type");
      return { type, integration: auto, services: [] };
    default:
      return assertNever(type);
  }
};

export const isValidApproval = (approval: ApprovalRule) => {
  switch (approval.type) {
    case "group":
      return approval.groups.every((g) => !!g.directory && !!g.id && !!g.label);
    default:
      return true;
  }
};

export const isValidResourceFilterType = (
  type: string,
  service: IntegrationResourceRule["service"]
) => {
  return Boolean(
    filterTypeOptionsByResource[service][
      type as ResourceFilterType<typeof service>
    ]
  );
};

export const generateFilterKeyOptions = (
  service: IntegrationResourceRule["service"],
  filterType: string
) => {
  const options = filterKeyOptionsByResource[service];
  if (!(filterType in options)) {
    return undefined;
  }
  return options[filterType as ResourceFilterType<typeof service>];
};

export const buildIdpGroupId = (group: IdpGroup) => {
  return `${group.directory}_${group.id}`;
};

export const parseIdpGroupId = (id: string): IdpGroup => {
  const [directory, idpId] = id.split("_");

  if (!directory || !idpId) {
    throw new Error("Invalid idp group id");
  }

  return {
    directory: directory as Directory,
    id: idpId,
    label: "",
  };
};

/** Deterministic hash of a routing rule, producing a short URL-safe string. */
export function hashRule(rule: RoutingRule): string {
  const str = stringify(rule) ?? "";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}
